/**
 * CVDocument.jsx  —  Professional ATS-readable PDF CV
 * Requires: npm install @react-pdf/renderer
 *
 * Bugs fixed from v1:
 *  ✓ Bullets merging — \s{2,} was collapsing \n\n, now uses [ \t]{2,}
 *  ✓ Arabic text "يامرحبا" rendered as garbage — stripped before PDF render
 *  ✓ 2-col skills grid causing row bleed — changed to clean 1-col rows
 *  ✓ Page 3 with 2 cert lines — tighter spacing + non-fixed header fits in 2 pages
 */

import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

/* ═══════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════ */

/** Strip non-Latin characters (Arabic, emoji, etc.)  — Helvetica can't render them */
const latinOnly = (str = '') =>
    str.replace(/[^\x00-\x7F]/g, '').replace(/\s{2,}/g, ' ').trim();

/** Extract <em>…</em> blocks before general strip — preserves italic notes */
const extractEmBlocks = (html = '') => {
    const notes = [];
    const cleaned = html.replace(/<em>([\s\S]*?)<\/em>/gi, (_, inner) => {
        const text = inner
            .replace(/<a[^>]*>([\s\S]*?)<\/a>/gi, '$1')
            .replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&')
            .trim();
        if (text) notes.push(text);
        return '';
    });
    return { cleaned, notes };
};

/**
 * Strip HTML tags and decode entities.
 * KEY FIX: uses [ \t]{2,} not \s{2,} — preserves \n line breaks.
 */
const stripHtml = (html = '') =>
    html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<a[^>]*>([\s\S]*?)<\/a>/gi, '$1')
        .replace(/<strong>([\s\S]*?)<\/strong>/gi, '$1')
        .replace(/<em>([\s\S]*?)<\/em>/gi, '$1')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/[ \t]{2,}/g, ' ')   // ← only collapse horizontal whitespace
        .trim();

/** Parse experience HTML → {bullets[], note} */
const parseDesc = (html = '') => {
    const { cleaned, notes } = extractEmBlocks(html);
    const lines = stripHtml(cleaned)
        .split('\n')
        .map(l => l.trim())
        .filter(Boolean);
    const raw     = lines.filter(l => /^[•·›\-\*]/.test(l));
    const bullets = (raw.length ? raw : lines)
        .map(b => b.replace(/^[•·›\-\*]\s*/, '').trim());
    const note = notes.join('  ·  ');
    return { bullets, note };
};

/** Parse summary HTML → clean paragraph strings (no Arabic, no contact lines) */
const parseSummary = (html = '') =>
    html
        .split(/<br\s*\/?>\s*<br\s*\/?>/gi)
        .map(p => latinOnly(stripHtml(p)))
        .filter(p =>
            p.length > 4 &&
            !p.includes('@') &&
            !p.toLowerCase().includes('linktree') &&
            !p.includes('linktr.ee') &&
            !/^📩/.test(p)
        );

/* ═══════════════════════════════════════════════════════════
   COLORS
═══════════════════════════════════════════════════════════ */
const C = {
    primary : '#0891b2',
    dark    : '#0f172a',
    mid     : '#334155',
    muted   : '#64748b',
    border  : '#e2e8f0',
    white   : '#ffffff',
    green   : '#059669',
    bg      : '#f8fafc',
};

/* ═══════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════ */
const s = StyleSheet.create({

    page: {
        fontFamily:      'Helvetica',
        fontSize:        8.5,
        color:           C.dark,
        paddingTop:      34,
        paddingBottom:   44,
        paddingLeft:     42,
        paddingRight:    42,
        lineHeight:      1.45,
        backgroundColor: C.white,
    },

    /* ── Header — full-width stacked (no two-column misalignment) ── */
    header: {
        paddingBottom:     10,
        marginBottom:      12,
        borderBottomWidth: 2.5,
        borderBottomColor: C.primary,
    },
    name: {
        fontSize:      22,
        fontFamily:    'Helvetica-Bold',
        color:         C.dark,
        letterSpacing: -0.3,
        marginBottom:  3,
    },
    rolesLine: {
        fontSize:      8.5,
        fontFamily:    'Helvetica-Bold',
        color:         C.primary,
        letterSpacing: 0.2,
        marginBottom:  3,
    },
    tagline: {
        fontSize:     8,
        color:        C.muted,
        marginBottom: 6,
    },
    /* Contact items in one horizontal row, separated by pipes */
    contactRow: {
        flexDirection: 'row',
        flexWrap:      'wrap',
        alignItems:    'center',
    },
    contactItem: {
        fontSize:  7.5,
        color:     C.mid,
        marginRight: 10,
    },
    contactSep: {
        fontSize:    7.5,
        color:       C.border,
        marginRight: 10,
    },

    /* ── Section ── */
    section:      { marginBottom: 10 },
    sectionTitle: {
        fontSize:          6.5,
        fontFamily:        'Helvetica-Bold',
        color:             C.primary,
        letterSpacing:     2,
        marginBottom:      5,
        paddingBottom:     3,
        borderBottomWidth: 0.5,
        borderBottomColor: C.border,
    },

    /* ── Summary ── */
    summaryPara: {
        fontSize:     8.5,
        color:        C.mid,
        lineHeight:   1.6,
        marginBottom: 3.5,
    },
    summaryGoal: {
        fontSize:     8.5,
        fontFamily:   'Helvetica-Bold',
        color:        C.dark,
        marginBottom: 3,
    },

    /* ── Experience ── */
    entry:       { marginBottom: 7 },
    entryHeader: {
        flexDirection:  'row',
        justifyContent: 'space-between',
        alignItems:     'flex-start',
        marginBottom:   1,
    },
    entryTitle: {
        fontSize:    9,
        fontFamily:  'Helvetica-Bold',
        color:       C.dark,
        flex:        1,
        marginRight: 8,
        lineHeight:  1.3,
    },
    entryMeta: {
        fontSize:   7.5,
        color:      C.muted,
        textAlign:  'right',
        flexShrink: 0,
    },
    entrySub: {
        fontSize:     8,
        fontFamily:   'Helvetica-Bold',
        color:        C.primary,
        marginBottom: 3,
    },

    /* ── Bullets ── */
    bullet: {
        flexDirection: 'row',
        marginBottom:  1.5,
    },
    bulletMark: {
        width:      10,
        fontSize:   9,
        color:      C.primary,
        fontFamily: 'Helvetica-Bold',
        lineHeight: 1.4,
        flexShrink: 0,
    },
    bulletText: {
        flex:       1,
        fontSize:   8.5,
        color:      C.mid,
        lineHeight: 1.45,
    },
    noteText: {
        fontSize:   7.5,
        color:      C.muted,
        fontFamily: 'Helvetica-Oblique',
        marginTop:  2,
        paddingLeft: 10,
        lineHeight: 1.4,
    },

    /* ── Skills — clean 1-col rows, category left-aligned ── */
    skillRow: {
        flexDirection: 'row',
        marginBottom:  2.5,
        alignItems:    'flex-start',
    },
    skillCat: {
        width:      112,
        flexShrink: 0,
        fontSize:   8,
        fontFamily: 'Helvetica-Bold',
        color:      C.dark,
        paddingTop: 0.5,
        lineHeight: 1.4,
    },
    skillVal: {
        flex:       1,
        fontSize:   8,
        color:      C.mid,
        lineHeight: 1.4,
    },
    skillFamiliar: {
        fontSize:   8,
        color:      C.muted,
        fontFamily: 'Helvetica-Oblique',
    },

    /* ── Volunteering ── */
    volEntry:    { marginBottom: 5.5 },
    volHeader:   { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 1 },
    volRole:     { fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: C.dark, flex: 1, marginRight: 6 },
    volMeta:     { fontSize: 7.5, color: C.muted, textAlign: 'right' },
    volOrg:      { fontSize: 8, fontFamily: 'Helvetica-Bold', color: C.primary, marginBottom: 1.5 },
    volDesc:     { fontSize: 8, color: C.mid, lineHeight: 1.45 },

    /* ── Two-column section: Education | Languages+Certs ── */
    twoCol:  { flexDirection: 'row' },
    colLeft: { flex: 3, paddingRight: 18 },
    colRight:{ flex: 2 },

    /* ── Education ── */
    eduEntry:    { marginBottom: 6 },
    eduDegree:   { fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: C.dark, marginBottom: 1 },
    eduInst:     { fontSize: 8, fontFamily: 'Helvetica-Bold', color: C.primary, marginBottom: 1 },
    eduDuration: { fontSize: 7.5, color: C.muted },

    /* ── Languages ── */
    langRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 3 },
    langName:  { fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: C.dark, marginRight: 4 },
    langLevel: { fontSize: 7.5, color: C.muted },

    /* ── Certifications ── */
    certItem:  { marginBottom: 3 },
    certTitle: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: C.dark, marginBottom: 0.5 },
    certMeta:  { fontSize: 7, color: C.muted },

    /* ── Divider ── */
    divider: {
        borderBottomWidth: 0.5,
        borderBottomColor: C.border,
        marginVertical:    5,
    },

    /* ── Footer (fixed — appears on every page) ── */
    footer: {
        position:       'absolute',
        bottom:         16,
        left:           42,
        right:          42,
        flexDirection:  'row',
        justifyContent: 'space-between',
        fontSize:        7,
        color:           C.muted,
        paddingTop:      4,
        borderTopWidth:  0.5,
        borderTopColor:  C.border,
    },
});

/* ═══════════════════════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════════════════════ */
const ST = ({ children }) => (
    <Text style={s.sectionTitle}>{String(children).toUpperCase()}</Text>
);

const Bullet = ({ text }) => (
    <View style={s.bullet}>
        <Text style={s.bulletMark}>›</Text>
        <Text style={s.bulletText}>{text}</Text>
    </View>
);

/* ═══════════════════════════════════════════════════════════
   DOCUMENT
═══════════════════════════════════════════════════════════ */
const CVDocument = ({ profile }) => {
    const summaryParas = parseSummary(profile.summary);

    return (
        <Document
            title={`${profile.name} — CV`}
            author={profile.name}
            subject="Curriculum Vitae"
            creator="Portfolio · alirajab12.github.io"
            keywords="Flutter, Software Engineer, Product Manager, Web3, SUI"
        >
            <Page size="A4" style={s.page}>

                {/* ══ HEADER — full-width stacked ══ */}
                <View style={s.header}>
                    <View><Text style={s.name}>{profile.name}</Text></View>
                    <View><Text style={s.rolesLine}>{profile.roles.join('  ·  ')}</Text></View>
                    <View><Text style={s.tagline}>{profile.tagline}</Text></View>
                    <View style={s.contactRow}>
                        {[
                            profile.contact.email,
                            profile.contact.phone,
                            profile.contact.github?.replace('https://github.com/', 'github.com/'),
                            profile.contact.linktree?.replace('https://', ''),
                            profile.location,
                        ]
                        .filter(Boolean)
                        .map((item, i, arr) => (
                            <React.Fragment key={i}>
                                <Text style={s.contactItem}>{item}</Text>
                                {i < arr.length - 1 && (
                                    <Text style={s.contactSep}>|</Text>
                                )}
                            </React.Fragment>
                        ))}
                    </View>
                </View>

                {/* ══ SUMMARY ══ */}
                <View style={s.section}>
                    <ST>Professional Summary</ST>
                    {summaryParas.map((para, i) => (
                        <Text
                            key={i}
                            style={para.startsWith('Goal:') ? s.summaryGoal : s.summaryPara}
                        >
                            {para}
                        </Text>
                    ))}
                </View>

                {/* ══ EXPERIENCE ══ */}
                <View style={s.section}>
                    <ST>Experience</ST>
                    {profile.experience.map((job, i) => {
                        const { bullets, note } = parseDesc(job.description);
                        return (
                            <View key={i} style={s.entry} wrap={bullets.length <= 3}>
                                <View style={s.entryHeader}>
                                    <Text style={s.entryTitle}>{job.job_title}</Text>
                                    <Text style={s.entryMeta}>
                                        {job.duration}{'  ·  '}{job.location}
                                    </Text>
                                </View>
                                <Text style={s.entrySub}>{job.company}</Text>
                                {bullets.map((b, j) => <Bullet key={j} text={b} />)}
                                {note ? <Text style={s.noteText}>{note}</Text> : null}
                            </View>
                        );
                    })}
                </View>

                {/* ══ SKILLS ══ */}
                <View style={s.section}>
                    <ST>Technical Skills</ST>
                    {Object.entries(profile.skills).map(([cat, data]) => {
                        const prof = Array.isArray(data.Proficient) ? data.Proficient : [];
                        const fam  = Array.isArray(data.Familiar)   ? data.Familiar   : [];
                        return (
                            <View key={cat} style={s.skillRow}>
                                <Text style={s.skillCat}>{cat}</Text>
                                <Text style={s.skillVal}>
                                    {prof.join(', ')}
                                    {fam.length > 0
                                        ? <Text style={s.skillFamiliar}>{`  (+  ${fam.join(', ')})`}</Text>
                                        : null}
                                </Text>
                            </View>
                        );
                    })}
                </View>

                {/* ══ VOLUNTEERING ══ */}
                <View style={s.section}>
                    <ST>Volunteering — 250+ Hours · United Nations & NGOs</ST>
                    {profile.volunteering.slice(0, 4).map((vol, i) => (
                        <View key={i} style={s.volEntry} wrap={false}>
                            <View style={s.volHeader}>
                                <Text style={s.volRole}>{vol.role}</Text>
                                <Text style={s.volMeta}>{vol.duration}</Text>
                            </View>
                            <Text style={s.volOrg}>
                                {vol.org}{vol.hours ? `  ·  ${vol.hours}` : ''}
                            </Text>
                            <Text style={s.volDesc} numberOfLines={2}>
                                {vol.description}
                            </Text>
                        </View>
                    ))}
                </View>

                {/* ══ EDUCATION  |  LANGUAGES + CERTS ══ */}
                <View style={[s.section, s.twoCol]}>

                    <View style={s.colLeft}>
                        <ST>Education</ST>
                        {profile.education.map((edu, i) => (
                            <View key={i} style={s.eduEntry} wrap={false}>
                                <Text style={s.eduDegree}>{edu.degree}</Text>
                                <Text style={s.eduInst}>{edu.institution}</Text>
                                <Text style={s.eduDuration}>{edu.duration}</Text>
                            </View>
                        ))}
                    </View>

                    <View style={s.colRight}>
                        <ST>Languages</ST>
                        {profile.languages.map((lang, i) => {
                            const name  = typeof lang === 'string' ? lang : lang.name;
                            const level = typeof lang === 'string' ? ''   : lang.level;
                            return (
                                <View key={i} style={s.langRow}>
                                    <Text style={s.langName}>{name}</Text>
                                    {level ? <Text style={s.langLevel}>— {level}</Text> : null}
                                </View>
                            );
                        })}

                        <View style={s.divider} />

                        <ST>Certifications & Awards</ST>
                        {(profile.accomplishments?.certifications || []).map((cert, i) => (
                            <View key={i} style={s.certItem} wrap={false}>
                                <Text style={s.certTitle}>{cert.title}</Text>
                                <Text style={s.certMeta}>
                                    {cert.issuer}{'  ·  '}{cert.date}
                                </Text>
                            </View>
                        ))}
                    </View>
                </View>

                {/* ══ FOOTER — fixed on every page ══ */}
                <View style={s.footer} fixed>
                    <Text>{profile.name}  ·  Curriculum Vitae</Text>
                    <Text render={({ pageNumber, totalPages }) =>
                        `Page ${pageNumber} of ${totalPages}`}
                    />
                </View>

            </Page>
        </Document>
    );
};

export default CVDocument;
